import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  HeartIcon,
  ShareIcon,
  StarIcon,
  MinusIcon,
  PlusIcon,
  TruckIcon,
  ShieldCheckIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { Product } from '../../../features/products/productModels';
import './ProductInfo.css';

interface ProductInfoProps {
  product: Product;
  quantity: number;
  isWishlisted: boolean;
  onQuantityChange: (quantity: number) => void;
  onAddToCart: () => void;
  onWishlistToggle: () => void;
  getProductPrice: () => number;
  getProductName: () => string;
  getProductBrand: () => string;
  getProductModel: () => string;
  getProductDescription: () => string;
  getCategoryName: () => string;
  isProductEnabled: () => boolean;
  isProductOnSale: () => boolean;
  getSaleDescription: () => string;
  getProductSalePrice: () => number;
}

const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  quantity,
  isWishlisted,
  onQuantityChange,
  onAddToCart,
  onWishlistToggle,
  getProductPrice,
  getProductName,
  getProductBrand,
  getProductModel,
  getProductDescription,
  getCategoryName,
  isProductEnabled,
  isProductOnSale,
  getSaleDescription,
  getProductSalePrice
}) => {
  const { t } = useTranslation();
  
  const renderStars = (rating: string | number) => {
    const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
    return Array.from({ length: 5 }, (_, index) => (
      <StarIcon
        key={index}
        className={`star ${index < Math.floor(numRating) ? 'filled' : ''}`}
      />
    ));
  };

  return (
    <div className="product-info">
      <div className="product-header">
        <div className="product-brand">{getProductBrand()}{getProductModel() && ` - ${getProductModel()}`}</div>
        <h1 className="product-title">{getProductName()}</h1>
        
        <div className="product-rating">
          <div className="stars">
            {renderStars(5)} {/* Default rating since not in Product model */}
          </div>
          <span className="rating-text">
            {t('productDetail.ratingText', { rating: '5.0', count: 3 })}
          </span>
        </div>
      </div>

      <div className="product-price">
 
        <span className="current-price">₪{ product?.salePrice && product?.salePrice>0 && product.sale ? product.salePrice : product.price}</span>
        {isProductOnSale() && (
          <>
            {product.sale && (
              <span className="original-price">₪{product.price}</span>
            )}
            {getSaleDescription() && (
              <span className="sale-description" style={{ fontWeight: 'bold' }}>
                {getSaleDescription()}
              </span>
            )}
          </>
        )}
      </div>

      <div className="product-description">
        <p>{getProductDescription()}</p>
      </div>

      <div className="product-actions">
        <div className="quantity-selector">
          <button 
            className="quantity-button"
            onClick={() => onQuantityChange(quantity - 1)}
            disabled={quantity <= 1}
            aria-label={t('productDetail.decreaseQuantity')}
          >
            <MinusIcon className="quantity-icon" />
          </button>
          <span className="quantity">{quantity}</span>
          <button 
            className="quantity-button"
            onClick={() => onQuantityChange(quantity + 1)}
            disabled={quantity >= 10}
            aria-label={t('productDetail.increaseQuantity')}
          >
            <PlusIcon className="quantity-icon" />
          </button>
        </div>

        <button 
          className="add-to-cart-button"
          onClick={onAddToCart}
          disabled={!isProductEnabled()}
        >
          {isProductEnabled() ? t('productDetail.addToCart') : t('productDetail.outOfStock')}
        </button>

        <button 
          className={`wishlist-button ${isWishlisted ? 'active' : ''}`}
          onClick={onWishlistToggle}
          aria-label={isWishlisted ? t('productDetail.removeFromWishlist') : t('productDetail.addToWishlist')}
        >
          {isWishlisted ? (
            <HeartSolidIcon className="wishlist-icon" />
          ) : (
            <HeartIcon className="wishlist-icon" />
          )}
        </button>

        <button className="share-button" aria-label={t('productDetail.shareProduct')}>
          <ShareIcon className="share-icon" />
        </button>
      </div>

      <div className="product-features">
        <div className="feature">
          <TruckIcon className="feature-icon" />
          <span>{t('productDetail.freeShippingOver', { amount: 50 })}</span>
        </div>
        <div className="feature">
          <ShieldCheckIcon className="feature-icon" />
          <span>{t('productDetail.moneyBackGuarantee')}</span>
        </div>
        <div className="feature">
          <ArrowPathIcon className="feature-icon" />
          <span>{t('productDetail.easyReturns')}</span>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;
